import * as Sentry from '@sentry/react';

// Enhanced error tracking utilities
export class ErrorTracker {
  // Track network errors with detailed context
  static trackNetworkError(error: any, requestInfo: {
    url: string;
    method: string;
    status?: number;
    statusText?: string;
    headers?: any;
    data?: any;
    duration?: number;
    errorType?: string;
    identifier?: string;
    isTimeout?: boolean;
    isNetworkError?: boolean;
    isServerError?: boolean;
    isClientError?: boolean;
    [key: string]: any; 
  }) {
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'network');
      scope.setLevel('error');
      
      scope.setContext('network_request', {
        url: requestInfo.url,
        method: requestInfo.method,
        status: requestInfo.status,
        statusText: requestInfo.statusText,
        timestamp: new Date().toISOString(),
      });
      
      if (requestInfo.headers) {
        scope.setContext('request_headers', requestInfo.headers);
      }
      
      if (requestInfo.data) {
        scope.setContext('request_data', requestInfo.data);
      }
      
      // Add breadcrumb for network error
      Sentry.addBreadcrumb({
        category: 'http',
        message: `Network error: ${requestInfo.method} ${requestInfo.url}`,
        level: 'error',
        data: {
          status: requestInfo.status,
          url: requestInfo.url,
          method: requestInfo.method,
        },
      });
      
      Sentry.captureException(error);
    });
  }
  
  // Track click errors and failed user interactions
  static trackClickError(error: any, element: Element | null, action: string) {
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'click');
      scope.setLevel('error');
      
      if (element) {
        scope.setContext('click_element', {
          tagName: element.tagName,
          className: element.className,
          id: element.id,
          textContent: element.textContent?.substring(0, 100),
          outerHTML: element.outerHTML?.substring(0, 200),
        });
      }
      
      scope.setContext('user_action', {
        action,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });
      
      // Add breadcrumb for click error
      Sentry.addBreadcrumb({
        category: 'ui',
        message: `Click error: ${action}`,
        level: 'error',
        data: {
          action,
          element: element?.tagName,
          className: element?.className,
        },
      });
      
      Sentry.captureException(error);
    });
  }
  
  // Track form submission errors
  static trackFormError(error: any, formData: FormData | any, formName?: string) {
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'form');
      scope.setLevel('error');
      
      // Sanitize form data (remove sensitive info)
      const sanitizedData: any = {};

      if (formData instanceof FormData) {
        for (const [key, value] of formData.entries()) {
          if (!this.isSensitiveField(key)) {
            sanitizedData[key] = typeof value === 'string' ? value.substring(0, 100) : '[File]';
          } else {
            sanitizedData[key] = '[REDACTED]';
          }
        }
      } else {
        Object.keys(formData).forEach(key => {
          if (!this.isSensitiveField(key)) {
            sanitizedData[key] = formData[key];
          } else {
            sanitizedData[key] = '[REDACTED]';
          }
        });
      }
      
      scope.setContext('form_submission', {
        formName: formName || 'unknown',
        fields: Object.keys(sanitizedData),
        data: sanitizedData,
        timestamp: new Date().toISOString(),
      });
      
      Sentry.captureException(error);
    });
  }
  
  // Track performance issues
  static trackPerformanceIssue(issue: {
    type: 'slow_load' | 'memory_leak' | 'render_issue' | 'bundle_size';
    metric: string;
    value: number;
    threshold: number;
    details?: any;
  }) {
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'performance');
      scope.setLevel('warning');
      
      scope.setContext('performance_issue', {
        type: issue.type,
        metric: issue.metric,
        value: issue.value,
        threshold: issue.threshold,
        exceedsThreshold: issue.value > issue.threshold,
        details: issue.details,
        timestamp: new Date().toISOString(),
      });
      
      // Add performance data
      if (window.performance) {
        scope.setContext('navigation_timing', {
          loadEventEnd: window.performance.timing?.loadEventEnd,
          domContentLoadedEventEnd: window.performance.timing?.domContentLoadedEventEnd,
          responseEnd: window.performance.timing?.responseEnd,
        });
      }
      
      Sentry.captureMessage(`Performance issue: ${issue.type} - ${issue.metric} exceeded threshold`, 'warning');
    });
  }
  
  // Track console errors that might be missed
  static trackConsoleError(error: any, level: 'error' | 'warn' = 'error') {
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'console');
      scope.setLevel(level as Sentry.SeverityLevel);
      
      scope.setContext('console_error', {
        message: error.toString(),
        stack: error.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });
      
      if (level === 'error') {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(error.toString(), level as Sentry.SeverityLevel);
      }
    });
  }

  // Track async operation failures
  static trackAsyncError(error: any, operation: string, context?: any) {
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'async');
      scope.setLevel('error');
      
      scope.setContext('async_operation', {
        operation,
        context,
        timestamp: new Date().toISOString(),
      });
      
      Sentry.addBreadcrumb({
        category: 'async',
        message: `Async operation failed: ${operation}`,
        level: 'error',
        data: { operation,
          context },
      });
      
      Sentry.captureException(error);
    });
  }
  
  // Track route/navigation errors
  static trackNavigationError(error: any, route: string, previousRoute?: string) {
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'navigation');
      scope.setLevel('error');
      
      scope.setContext('navigation', {
        route,
        previousRoute,
        timestamp: new Date().toISOString(),
      });
      
      Sentry.captureException(error);
    });
  }
  
  // Helper to identify sensitive form fields
  private static isSensitiveField(fieldName: string): boolean {
    const sensitiveFields = [
      'password', 'passwd', 'pwd',
      'token', 'secret', 'key',
      'credit', 'card', 'cvv', 'cvc',
      'ssn', 'social',
      'api_key', 'auth_token',
    ];
    
    return sensitiveFields.some(sensitive => fieldName.toLowerCase().includes(sensitive),
    );
  }
}

// Global error handlers
export const setupGlobalErrorHandlers = () => {
  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    ErrorTracker.trackAsyncError(
      event.reason,
      'unhandled_promise_rejection',
      { promise: event.promise },
    );
  });
  
  // Capture global errors
  window.addEventListener('error', (event) => {
    ErrorTracker.trackConsoleError(event.error || event);
  });
  
  // Override console.error to capture console errors
  const originalConsoleError = console.error;
  console.error = (...args) => {
    originalConsoleError.apply(console, args);

    if (args.length > 0 && args[0] instanceof Error) {
      ErrorTracker.trackConsoleError(args[0]);
    }
  };
};

// Performance monitoring utilities
export const trackPageLoad = () => {
  if (window.performance && window.performance.timing) {
    const timing = window.performance.timing;
    const loadTime = timing.loadEventEnd - timing.navigationStart;
    const domReady = timing.domContentLoadedEventEnd - timing.navigationStart;
    
    // Track slow page loads
    if (loadTime > 3000) { 
      ErrorTracker.trackPerformanceIssue({
        type: 'slow_load',
        metric: 'page_load_time',
        value: loadTime,
        threshold: 3000,
        details: {
          loadTime,
          domReady,
          url: window.location.href,
        },
      });
    }
  }
};

// Memory usage monitoring
export const trackMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    const usedHeap = memory.usedJSHeapSize;
    const totalHeap = memory.totalJSHeapSize;
    const heapLimit = memory.jsHeapSizeLimit;
    
    // Track high memory usage (>80% of limit)
    if (usedHeap > heapLimit * 0.8) {
      ErrorTracker.trackPerformanceIssue({
        type: 'memory_leak',
        metric: 'heap_usage',
        value: usedHeap,
        threshold: heapLimit * 0.8,
        details: {
          usedHeap,
          totalHeap,
          heapLimit,
          usagePercentage: (usedHeap / heapLimit) * 100,
        },
      });
    }
  }
}; 